import React, { useEffect } from "react";
import Button from "@mui/material/Button";
import styles from "./ProcessFeeMasterDialog.module.scss";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DehazeIcon from "@mui/icons-material/Dehaze";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  ResponderProvided,
} from "react-beautiful-dnd";
import { getBrandInvoices } from "../../../apis/brand-api";
import { InvoiceListResponseData } from "../../../apis/models/InvoiceMasterRequest";
import {
  getProcessFeeOptions,
  postProcessFeeOptions,
} from "../../../apis/invoice-api";
import {
  ProcessFeeDetailsResponseDataOption,
  ProcessFeeOption,
} from "../../../apis/models/ProcessFeeDetailsResponse";
import { InvStatus } from "../../../libs/constants";
import InputCell from "../../core/InputCell";
import {
  ProcessFeeOptionRequest,
  ProcessFeeOptionRequestItem,
} from "../../../apis/models/ProcessFeeRequest";

type RowType = {
  isAddedRow?: boolean;
} & ProcessFeeOption;

const COL_WORK = "work";
const COL_PRICE = "price";


const ProcessFeeMasterDialog: React.FC<ProcessFeeMasterDialogProps> = (
  props: ProcessFeeMasterDialogProps
) => {
  const { open, brandNo, invoiceMasterId, onClose, onSuccess, onCancel } =
    props;
  const sortData = React.useRef<{ col: string; asc: boolean }>({
    col: "",
    asc: false,
  });

  const [masterDataList, setMasterDataList] = React.useState<RowType[]>([]);
  const [invoices, setInvoices] = React.useState<InvoiceListResponseData[]>([]);
  const [saveDisabled, setSaveDisabled] = React.useState<boolean>(true);
  const [updating, setUpdating] = React.useState<boolean>(false);


  useEffect(() => {
    let canceled = false;
    getBrandInvoices(brandNo)
      .then(function (invoiceResponse) {
        const brandInvoices: Array<InvoiceListResponseData> =
          invoiceResponse.data.data;
        if (!brandInvoices || brandInvoices.length <= 0 || canceled) {
          return;
        }

        setInvoices(
          brandInvoices
            .filter((inv) => inv.status === InvStatus.NOT_CONFIRMED)
            .sort((a, b) => (a.to < b.to ? -1 : 1))
        );

        getProcessFeeOptions(invoiceMasterId)
          .then(function (masterResponse) {
            const processFeeOptions: Array<ProcessFeeOption> = masterResponse
              .data.data
              ? masterResponse.data.data
              : [];
            if (!canceled) {
              setMasterDataList(
                processFeeOptions.sort((a, b) => a.order! - b.order!)
              );
            }
          })
          .catch(function (error) {
            console.log(error);
          });
      })
      .catch(function (error) {
        console.log(error);
      });

    return () => {
      canceled = true;
    };
  }, [brandNo, invoiceMasterId]);

  const isRowError = (data: ProcessFeeDetailsResponseDataOption): boolean => {
    return data.workDetails.length <= 0 || data.unitPrice <= 0;
  };

  useEffect(() => {
    setSaveDisabled(
      masterDataList.findIndex((master) =>
        isRowError(master.processFeeOption)
      ) >= 0
    );
  }, [masterDataList]);

  const onSort = (colName: string) => {
    if (sortData.current.col === colName) {
      sortData.current.asc = !sortData.current.asc;
    } else {
      sortData.current.col = colName;
      sortData.current.asc = true;
    }

    if (colName === COL_WORK) {
      setMasterDataList((prev) =>
        [...prev].sort(
          (a, b) =>
            a.processFeeOption.workDetails.localeCompare(
              b.processFeeOption.workDetails
            ) * (sortData.current.asc ? 1 : -1)
        )
      );
    } else if (colName === COL_PRICE) {
      setMasterDataList((prev) =>
        [...prev].sort(
          (a, b) =>
            (a.processFeeOption.unitPrice - b.processFeeOption.unitPrice) *
            (sortData.current.asc ? 1 : -1)
        )
      );
    }
  };

  const onDragEnd = (result: DropResult, provide: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    const targetData = masterDataList[result.source.index];
    const newData = [...masterDataList];
    newData.splice(result.source.index, 1);
    newData.splice(result.destination.index, 0, targetData);

    setMasterDataList(newData);
  };

  const onAddRow = () => {
    const data: RowType = {
      isAddedRow: true,
      processFeeOption: {
        unitPrice: 0,
        workDetails: "",
      },
    };
    setMasterDataList((prev) => [data].concat(prev));
  };

  const onDeleteRow = (rowNumber: number) => {
    setMasterDataList((prev) =>
      [...prev].filter((master, index) => index !== rowNumber)
    );
  };

  const onChangeRowData = (
    target: string,
    data: string | number,
    rowNumber: number
  ) => {
    setMasterDataList((prev) => {
      const newData = [...prev];
      if (target === COL_WORK) {
        newData[rowNumber].processFeeOption.workDetails = data as string;
      } else if (target === COL_PRICE) {
        newData[rowNumber].processFeeOption.unitPrice = data as number;
      }
      return newData;
    });
  };

  const onSave = () => {
    const processFeeOptions = masterDataList.map((master, index) => {
      const data: ProcessFeeOptionRequestItem = {
        id: master.processFeeOption.processFeeOptionId,
        unitPrice: master.processFeeOption.unitPrice,
        workDetails: master.processFeeOption.workDetails,
        order: index,
      };
      return data;
    });
    const req: ProcessFeeOptionRequest = {
      processFeeOptions: processFeeOptions,
    };

    setUpdating(true);
    postProcessFeeOptions(invoiceMasterId, req)
      .then(function () {
        onSuccess();
      })
      .catch(function (error) {
        console.log(error);
        alert("保存に失敗しました。");
      })
      .finally(() => setUpdating(false));
  };


  return (
    <Dialog open={open} onClose={() => !updating && onClose()} maxWidth="xl">
      <DialogTitle
        sx={{ fontWeight: 700, fontSize: "24px", padding: "40px 40px 24px" }}
      >
        加工作業内容管理
      </DialogTitle>
      <DialogContent sx={{ padding: "0 40px 24px" }}>
        <div className={styles.result}>
          {updating ? (
            <div>
              <CircularProgress />
            </div>
          ) : (
            <div>
              <div className={styles.table_wrapper}>
                <table className={styles.result_table}>
                  <thead>
                    <tr>
                      <th></th>
                      <th
                        className={styles.sortable}
                        onClick={() => onSort(COL_WORK)}
                      >
                        作業内容
                      </th>
                      <th
                        className={styles.sortable}
                        onClick={() => onSort(COL_PRICE)}
                      >
                        単価
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <DragDropContext
                    onDragEnd={(result, provide) => onDragEnd(result, provide)}
                  >
                    <Droppable droppableId="droppable">
                      {(provided, snapshot) => (
                        <tbody
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {masterDataList.map((item, index) => (
                            <Draggable
                              key={index}
                              draggableId={`draggableId${index}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <td
                                    className={styles.result_col1}
                                    {...provided.dragHandleProps}
                                  >
                                    <DehazeIcon color="primary" />
                                  </td>
                                  <td className={styles.result_col2}>
                                    <InputCell
                                      value={item.processFeeOption.workDetails}
                                      type={"text"}
                                      onChangeText={(data) =>
                                        onChangeRowData(COL_WORK, data, index)
                                      }
                                      isConfirmed={false}
                                      textAlignment="left"
                                    />
                                  </td>
                                  <td className={styles.result_col3}>
                                    <InputCell
                                      value={item.processFeeOption.unitPrice}
                                      type={"number"}
                                      onChangeText={(data) =>
                                        onChangeRowData(COL_PRICE, data, index)
                                      }
                                      isConfirmed={false}
                                      textAlignment="right"
                                    />
                                  </td>
                                  <td
                                    className={styles.result_col4}
                                    onClick={() => onDeleteRow(index)}
                                  >
                                    <CloseIcon
                                      color="primary"
                                      fontSize="small"
                                      className={styles.delete_icon}
                                    />
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  </DragDropContext>
                </table>
              </div>
              <div className={styles.information}>
                {invoices.length > 0 ? (
                  <div>
                    <div>下記の請求にも変更が反映される。</div>
                    {invoices.map((item, index) => (
                      item.year !== null && item.month !== null ?
                      <div
                        key={index}
                      >{`${item.year}年${item.month}月請求`}</div>
                      :<div></div>
                    ))}
                  </div>
                ) : (
                  <div></div>
                )}
                <div className={styles.add_button}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => onAddRow()}
                  >
                    ＋行追加
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className={styles.functions}>
            <Button
              variant="outlined"
              onClick={() => onCancel()}
              className={styles.btn}
              disabled={updating}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={() => onSave()}
              className={styles.btn}
              disabled={saveDisabled || updating}
            >
              保存
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessFeeMasterDialog;

export type ProcessFeeMasterDialogProps = {
  open: boolean;
  brandNo: string;
  invoiceMasterId: string;
  onClose: () => void;
  onCancel: () => void;
  onSuccess: () => void;
};
